import React from 'react';
import PropTypes from 'prop-types';
import format from 'date-fns/format';

import pick from 'lodash/pick';

// Multibrand-UI Components
import DatePicker from '@zg-rentals/multibrand-ui/lib/DatePicker';

// Constellation Components
import {
    AdornedInput,
    Adornment,
    Button,
    Checkbox,
    FormField,
    FormHelp,
    Input,
    Label,
    LabeledControl,
    Switch,
    Text,
} from '@zillow/constellation';

import {
    Col,
    Link,
    Row,
} from '@zillow/zrs-comet';

// constants
import KeyCodeConstants from 'app/shared/constants/KeyCodeConstants';

// Styled Components
import {
    StyledEditUnitModalDiv,
} from './StyledComponents';

const emptyUnit = {
    name: '',
    sqft: '',
    dateAvailable: '',
    price: '',
    active: true,
};

const propTypes = {
    defaultUnit: PropTypes.shape({
        name: PropTypes.string,
        sqft: PropTypes.number,
        dateAvailable: PropTypes.string,
        price: PropTypes.number,
        active: PropTypes.bool,
    }),
    onSubmit: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
};

const defaultProps = {
    defaultUnit: emptyUnit,
};

const EditUnitModal = ({
    defaultUnit,
    onSubmit,
    onCancel,
}) => {
    const [unit, setUnit] = React.useState(defaultUnit);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [unitErrors, setUnitErrors] = React.useState({});
    const [genericError, setGenericError] = React.useState();

    const handleSubmit = () => {
        setIsSubmitting(true);
        onSubmit(pick(unit, Object.keys(emptyUnit)))
            .catch((result) => {
                setIsSubmitting(false);
                if (result.error && result.error.data && result.error.data.errorByFieldName) {
                    setUnitErrors(result.error.data.errorByFieldName);
                } else {
                    setGenericError('Something went wrong, please try again later.');
                }
            });
    };

    const handleUnitChange = (key, value) => {
        setGenericError();
        setUnit({
            ...unit,
            [key]: value,
        });
    };

    const onKeyUp = (event) => {
        if (event.keyCode === KeyCodeConstants.ENTER ||
            event.keyCode === KeyCodeConstants.SPACE) {
            event.target.click();
        }
    };

    return (
        <StyledEditUnitModalDiv>
            <div>
                <Row paddingY="xs">
                    <Col>
                        <FormField>
                            <Label required={true}>Unit number</Label>
                            <Input
                                name="name"
                                placeholder="Unit number"
                                required={true}
                                error={unitErrors.name}
                                value={unit.name}
                                onChange={e => handleUnitChange('name', e.target.value)}
                            />
                            {unitErrors.name && (
                                <FormHelp error={true}>{unitErrors.name}</FormHelp>)}
                        </FormField>
                    </Col>
                </Row>
                <Row paddingY="xs">
                    <Col>
                        <FormField>
                            <Label>Square feet</Label>
                            <Input
                                error={unitErrors.squareFeet}
                                name="sqft"
                                value={unit.sqft}
                                type="number"
                                onChange={e => handleUnitChange('sqft', e.target.value)}
                            />
                            {unitErrors.squareFeet && (
                                <FormHelp error={true}>{unitErrors.squareFeet}</FormHelp>)}
                        </FormField>
                    </Col>
                </Row>
                <Row paddingY="xs">
                    <Col>
                        <FormField>
                            <Label required={true}>Price</Label>
                            <AdornedInput
                                input={(
                                    <Input
                                        error={unitErrors.price}
                                        name="price"
                                        value={unit.price}
                                        onChange={e => handleUnitChange('price', e.target.value)}
                                    />
                                )}
                                leftAdornment={unit.price && <Adornment>$</Adornment>}
                                rightAdornment={<Adornment>/mo</Adornment>}
                            />
                            <FormHelp>Note: Units without prices will not appear on Zillow.</FormHelp>
                            {unitErrors.price && (
                                <FormHelp error={true}>{unitErrors.price}</FormHelp>)}
                        </FormField>
                    </Col>
                </Row>
                <Row paddingY="xs">
                    <Col>
                        <DatePicker
                            id="unit-date"
                            label="Date Available"
                            value={unit.dateAvailable ? new Date(`${unit.dateAvailable}T10:00:00`) : null}
                            onChange={e => handleUnitChange('dateAvailable',
                                format(new Date(e.target.value), 'YYYY-MM-DD'))}
                            error={unitErrors.dateAvailable}
                            closeOnSelectDate={true}
                        />
                    </Col>
                </Row>
                <Row paddingY="xs">
                    <Col>
                        <div className="EditUnitModal-switch-container">
                            <LabeledControl
                                aria-label="Unit available"
                                tabIndex={0}
                                role="button"
                                size="lg"
                                controlId="Unit-available"
                                label={(<Label>Unit available</Label>)}
                                labelPosition="left"
                                control={(
                                    <Switch
                                        className="EditUnitModal-switch"
                                        checkbox={(
                                            <Checkbox
                                                checked={unit.active}
                                                onClick={() => handleUnitChange('active', !unit.active)}
                                                onKeyUp={onKeyUp}
                                            />
                                        )}
                                    />
                                )}
                            />
                        </div>
                        {unitErrors.active &&
                        <FormHelp>{unitErrors.active}</FormHelp>
                        }
                    </Col>
                </Row>
                {genericError && (
                    <Row paddingY="xs">
                        <Col>
                            <Text fontColor="caution" fontType="body">
                                {genericError}
                            </Text>
                        </Col>
                    </Row>
                )}
            </div>
            <div className="EditUnitModal-controls">
                <Link
                    aria-label="Cancel"
                    tabIndex={0}
                    role="button"
                    className="EditUnitModal-cancel"
                    onClick={onCancel}
                    onKeyUp={onKeyUp}
                    disabled={isSubmitting}
                >
                    Cancel
                </Link>
                <Button
                    aria-label="Save"
                    onClick={handleSubmit}
                    disabled={!unit.name || isSubmitting}
                    buttonType="primary"
                >
                    Save
                </Button>
            </div>
        </StyledEditUnitModalDiv>
    );
};

EditUnitModal.propTypes = propTypes;
EditUnitModal.defaultProps = defaultProps;

export { EditUnitModal };
export default EditUnitModal;
